import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AiUsageReport, AiUsageSource } from "@/types/admin/ai-usage";
import { formatCount, formatTokens, formatUsd, SOURCE_COLORS, SOURCE_LABELS } from "./constants";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const SOURCES: AiUsageSource[] = ["edge", "workflow"];
const DAY_MS = 86_400_000;
// ai_usage drops the account from usage older than this (condense_ai_usage).
const ACCOUNT_RETENTION_DAYS = 30;

const shortDate = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const SourceChip = ({ source }: { source: AiUsageSource }) => (
  <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
    <span className="h-2 w-2 rounded-full" style={{ background: SOURCE_COLORS[source] }} />
    {SOURCE_LABELS[source]}
  </span>
);

const StatTile = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="rounded-2xl border border-[#E5EAF0] bg-white p-4 shadow-sm md:p-5">
    <div className="text-xs font-medium uppercase tracking-wide text-[#64748B]">{label}</div>
    <div className="mt-1.5 text-2xl font-bold tabular-nums text-[#222B45] md:text-3xl">{value}</div>
    {hint && <div className="mt-1 text-xs text-[#64748B]">{hint}</div>}
  </div>
);

const Card = ({ title, genieIcon, children }: { title: string; genieIcon: GenieIconName; children: React.ReactNode }) => (
  <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-sm">
    <div className="flex items-center gap-2.5 border-b border-[#E5EAF0] px-4 py-3 md:px-5">
      <GenieIcon name={genieIcon} size={20} gradientEnabled secondaryColor="#FF4DB8" label="" />
      <span className="text-sm font-semibold text-[#222B45]">{title}</span>
    </div>
    {children}
  </div>
);

const DailyTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="mb-1.5 font-medium text-[#222B45]">{shortDate(label)}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-sm text-[#222B45]">
          <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />
          {SOURCE_LABELS[p.dataKey as AiUsageSource]}: <span className="font-semibold tabular-nums">{formatUsd(p.value)}</span>
        </p>
      ))}
      <p className="mt-1.5 border-t border-[#E5EAF0] pt-1.5 text-sm text-[#64748B]">
        Total <span className="font-semibold tabular-nums text-[#222B45]">{formatUsd(total)}</span>
      </p>
    </div>
  );
};

const UsageTab = ({ report }: { report: AiUsageReport }) => {
  const { totals, byFeature, byDay, byUser } = report;
  const accountsCapped = Date.now() - new Date(report.from).getTime() > (ACCOUNT_RETENTION_DAYS + 1) * DAY_MS;

  const daily = useMemo(() => {
    const costs = new Map(byDay.map((d) => [`${d.day}:${d.source}`, d.costUsd]));
    const days: Array<{ day: string } & Record<AiUsageSource, number>> = [];
    const start = Date.parse(report.from.slice(0, 10));
    for (let t = start; t <= Date.parse(report.to.slice(0, 10)); t += DAY_MS) {
      const day = new Date(t).toISOString().slice(0, 10);
      days.push({ day, edge: costs.get(`${day}:edge`) ?? 0, workflow: costs.get(`${day}:workflow`) ?? 0 });
    }
    return days;
  }, [byDay, report.from, report.to]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Estimated cost"
          value={formatUsd(totals.costUsd)}
          hint={totals.unpricedCalls > 0 ? `${formatCount(totals.unpricedCalls)} calls without a listed price` : undefined}
        />
        <StatTile
          label="Input tokens"
          value={formatTokens(totals.inputTokens)}
          hint={totals.cacheReadTokens > 0 ? `${formatTokens(totals.cacheReadTokens)} read from cache` : undefined}
        />
        <StatTile label="Output tokens" value={formatTokens(totals.outputTokens)} />
        <StatTile label="Model calls" value={formatCount(totals.calls)} />
      </div>

      <Card title="Cost per day" genieIcon="trending-up">
        <div className="flex flex-wrap items-center gap-4 px-4 pt-3 md:px-5">
          {SOURCES.map((source) => (
            <span key={source} className="flex items-center gap-1.5 text-xs text-[#64748B]">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: SOURCE_COLORS[source] }} />
              {SOURCE_LABELS[source]}
            </span>
          ))}
        </div>
        <div className="h-64 px-2 pb-3 pt-2 md:px-3">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="day"
                tickFormatter={shortDate}
                tick={{ fontSize: 11, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) => formatUsd(v)}
                tick={{ fontSize: 11, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip content={<DailyTooltip />} cursor={{ fill: "#4E9CFF", fillOpacity: 0.06 }} />
              <Bar dataKey="edge" stackId="cost" fill={SOURCE_COLORS.edge} stroke="#FFFFFF" strokeWidth={1} />
              <Bar dataKey="workflow" stackId="cost" fill={SOURCE_COLORS.workflow} stroke="#FFFFFF" strokeWidth={1} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="By feature and model" genieIcon="dashboard">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5EAF0] bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
                <th className="px-4 py-2.5 md:px-5">Feature</th>
                <th className="px-4 py-2.5 md:px-5">Model</th>
                <th className="px-4 py-2.5 text-right md:px-5">Calls</th>
                <th className="px-4 py-2.5 text-right md:px-5">Input</th>
                <th className="px-4 py-2.5 text-right md:px-5">Output</th>
                <th className="px-4 py-2.5 text-right md:px-5">Cost</th>
                <th className="px-4 py-2.5 md:px-5">Function</th>
              </tr>
            </thead>
            <tbody>
              {byFeature.map((row) => (
                <tr key={`${row.feature}:${row.source}:${row.provider}:${row.model}`} className="border-b border-[#E5EAF0] last:border-0">
                  <td className="px-4 py-3 md:px-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[13px] text-[#222B45]">{row.feature}</span>
                      <SourceChip source={row.source} />
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[#64748B] md:px-5">{row.model}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatCount(row.calls)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatTokens(row.inputTokens)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatTokens(row.outputTokens)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-[#222B45] md:px-5">
                    {row.unpricedCalls === row.calls ? <span className="font-normal text-[#64748B]">No price</span> : formatUsd(row.costUsd)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[#222B45] md:px-5">
                    {row.edgeFunction ?? <span className="font-sans text-[#64748B]">GitHub Actions</span>}
                  </td>
                </tr>
              ))}
              {byFeature.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#64748B]">
                    No AI usage recorded for this range yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={accountsCapped ? `Top accounts · last ${ACCOUNT_RETENTION_DAYS} days` : "Top accounts"} genieIcon="user">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5EAF0] bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
                <th className="px-4 py-2.5 md:px-5">Account</th>
                <th className="px-4 py-2.5 text-right md:px-5">Calls</th>
                <th className="px-4 py-2.5 text-right md:px-5">Tokens</th>
                <th className="px-4 py-2.5 text-right md:px-5">Cost</th>
              </tr>
            </thead>
            <tbody>
              {byUser.map((row) => (
                <tr key={row.userId} className="border-b border-[#E5EAF0] last:border-0">
                  <td className="px-4 py-3 md:px-5">
                    <div className="font-medium text-[#222B45]">{row.fullName || row.email || row.userId}</div>
                    {row.fullName && row.email && <div className="text-xs text-[#64748B]">{row.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatCount(row.calls)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatTokens(row.tokens)}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#222B45] md:px-5">{formatUsd(row.costUsd)}</td>
                </tr>
              ))}
              {byUser.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-[#64748B]">
                    No usage attributed to an account in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default UsageTab;
